import { formatDistanceToNow } from 'date-fns'
import { Megaphone } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useCampaigns } from '@/api/hooks/use-campaigns'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import type { Campaign } from '@/types/newsletter'

const STATUS_TABS = ['all', 'draft', 'sent', 'scheduled'] as const

function rateColor(rate: number): string {
  if (rate >= 30) return 'text-green-600'
  if (rate >= 15) return 'text-yellow-600'
  return 'text-red-600'
}

function formatRate(numerator: number, denominator: number): string {
  if (denominator === 0) return '-'
  return `${((numerator / denominator) * 100).toFixed(1)}%`
}

export function CampaignsListPage(): React.JSX.Element {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useCampaigns({
    page,
    per_page: 20,
    status: statusFilter === 'all' ? undefined : statusFilter,
  })

  const campaigns = data?.data ?? []
  const pagination = data?.pagination

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (row: Campaign) => (
        <span className="flex items-center gap-2 font-medium text-text-primary">
          {row.name}
          {row.variants && row.variants.length > 0 && (
            <span className="rounded bg-brand-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-700">
              A/B
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'audience',
      label: 'Audience',
      render: (row: Campaign) => (
        <span className="text-sm text-text-secondary">{row.audience_name ?? '-'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: Campaign) => <StatusBadge status={row.status}>{row.status}</StatusBadge>,
    },
    {
      key: 'sent_date',
      label: 'Sent',
      render: (row: Campaign) => (
        <span className="text-sm text-text-secondary">
          {row.send_completed_at
            ? formatDistanceToNow(new Date(row.send_completed_at), { addSuffix: true })
            : '-'}
        </span>
      ),
    },
    {
      key: 'recipients',
      label: 'Recipients',
      render: (row: Campaign) => (
        <span className="text-sm">
          {row.total_recipients > 0 ? row.total_recipients.toLocaleString() : '-'}
        </span>
      ),
    },
    {
      key: 'open_rate',
      label: 'Open Rate',
      render: (row: Campaign) => {
        const rate = row.total_delivered > 0 ? (row.total_opened / row.total_delivered) * 100 : 0
        return (
          <span
            className={`text-sm font-medium ${row.total_delivered > 0 ? rateColor(rate) : 'text-text-tertiary'}`}
          >
            {formatRate(row.total_opened, row.total_delivered)}
          </span>
        )
      },
    },
    {
      key: 'click_rate',
      label: 'Click Rate',
      render: (row: Campaign) => {
        const rate = row.total_delivered > 0 ? (row.total_clicked / row.total_delivered) * 100 : 0
        return (
          <span
            className={`text-sm font-medium ${row.total_delivered > 0 ? rateColor(rate) : 'text-text-tertiary'}`}
          >
            {formatRate(row.total_clicked, row.total_delivered)}
          </span>
        )
      },
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">Campaigns</h1>
        <Link
          to="/campaigns/new"
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          + New Campaign
        </Link>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 border-b border-surface-3">
        {STATUS_TABS.map((tab) => (
          <button
            type="button"
            key={tab}
            onClick={() => {
              setStatusFilter(tab)
              setPage(1)
            }}
            className={`border-b-2 px-4 py-2.5 text-sm font-medium capitalize transition-colors ${
              statusFilter === tab
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {!isLoading && campaigns.length === 0 ? (
        <EmptyCampaigns />
      ) : (
        <DataTable
          columns={columns}
          data={campaigns}
          isLoading={isLoading}
          emptyMessage="No campaigns found"
          onRowClick={(row) =>
            navigate(row.status === 'draft' ? `/campaigns/${row.id}/edit` : `/campaigns/${row.id}`)
          }
          pagination={
            pagination
              ? { page: pagination.page, perPage: pagination.per_page, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
        />
      )}
    </div>
  )
}

function EmptyCampaigns(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Megaphone className="mb-4 h-12 w-12 text-text-tertiary" />
      <h3 className="text-lg font-medium text-text-primary">No campaigns yet</h3>
      <p className="mt-1 max-w-sm text-sm text-text-secondary">
        Create your first campaign to send newsletters to your audiences.
      </p>
      <Link
        to="/campaigns/new"
        className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Create your first campaign
      </Link>
    </div>
  )
}
